import { z } from 'zod';
import { parseSafeNumber } from '@/shared/lib/number';
import { VAT_RATE_DEFAULT } from '@/shared/lib/vat-constants';

// ---------------------------------------------------------------------------
// Typed Adjustment — replaces the legacy string|number adjustment field
// (CLAUDE.md rule 7: no string-modeled data; PDF-04 requirement)
// ---------------------------------------------------------------------------

export const TypedAdjustmentSchema = z.object({
  op: z.enum(['add', 'sub', 'mul']),
  value: z.number(),
});

export type TypedAdjustment = z.infer<typeof TypedAdjustmentSchema>;

export const RoomDimsSchema = z.object({
  length: z.coerce.number().default(0),
  width: z.coerce.number().default(0),
  height: z.coerce.number().default(0),
  manualFloorArea: z.coerce.number().optional(),
  manualWallArea: z.coerce.number().optional(),
  manualPerimeter: z.coerce.number().optional(),
  floorArea: z.coerce.number().optional(),
  wallArea: z.coerce.number().optional(),
  perimeter: z.coerce.number().optional(),
});

export const OpeningSchema = z.object({
  id: z.string().optional(),
  type: z.enum(['window', 'door']).default('window'),
  width: z.union([z.number(), z.string()]).default(0),
  height: z.union([z.number(), z.string()]).default(0),
  hasSlopes: z.boolean().default(false),
});

export const PartitionSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  length: z.union([z.number(), z.string()]).default(0),
  height: z.union([z.number(), z.string(), z.null()]).default(null),
  thickness: z.union([z.number(), z.string()]).default(0),
  ends: z.union([z.number(), z.string()]).default(1),
});

export const WallElementSchema = z.object({
  id: z.string().optional(),
  type: z.enum(['niche', 'ledge']).default('niche'),
  width: z.union([z.number(), z.string()]).default(0),
  height: z.union([z.number(), z.string()]).default(0),
  depth: z.union([z.number(), z.string()]).default(0),
  isFullHeight: z.boolean().default(false),
  includeInTotal: z.boolean().default(true),
});

export const EstimateItemSchema = z.object({
  id: z.string(),
  serviceId: z.string().default(''),
  name: z.string().default(''),
  nameKey: z.string().optional(),
  unit: z.string().default(''),
  unitType: z.string().optional(),
  categoryId: z.string().optional(),
  category: z.string().optional(),
  categoryName: z.string().optional(),
  priceLabor: z.coerce.number().default(0),
  qty: z.coerce.number().default(0),
  userQty: z.coerce.number().default(0),
  total: z.coerce.number().default(0),
  isManual: z.boolean().optional(),
  priceIsManual: z.boolean().optional().default(false),
  nameIsManual: z.boolean().optional().default(false),
  adjustment: TypedAdjustmentSchema.default({ op: 'mul', value: 1 }),
});

export const RoomSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  displayName: z.string().optional(),
  dims: RoomDimsSchema.default({ length: 0, width: 0, height: 0 }),
  openings: z.array(OpeningSchema).default([]),
  partitions: z.array(PartitionSchema).default([]),
  wallElements: z.array(WallElementSchema).default([]),
  items: z.array(EstimateItemSchema).default([]),
  /**
   * Per-material BOM overrides keyed by materialId (MAT-02).
   * Stores user-edited norm and qty flags so recompute does not clobber manual changes.
   * Optional with safe default {} for backward compatibility with pre-Phase-6 rooms.
   */
  bomOverrides: z.record(z.string(), z.object({
    normIsManual: z.boolean().default(false),
    /** WR-01: user-supplied norm override value. Present iff normIsManual=true. */
    norm: z.coerce.number().optional(),
    qty: z.coerce.number().optional(),
  })).optional().default({}),
  /**
   * Server-assigned version token for optimistic concurrency (CR-01, server-authoritative).
   * Set by syncBatch() to the server's `now` when the room is committed — NEVER from the
   * client's timestamp. Used as the room's serverVersion:
   *   - Clients send their last-known serverVersion as `baseVersion` in the next ChangeRecord.
   *   - If `baseVersion` matches the stored serverVersion → no concurrent change → apply.
   *   - If they differ → a concurrent server write happened → server wins.
   * Optional for backward compatibility — absent on rooms predating Phase 4 sync (treated as null → apply fresh).
   */
  lastModified: z.string().optional(),
}).superRefine((room, ctx) => {
  /**
   * GEOM-03 (D-Area4): Reject a room whose computed floor area would be negative.
   *
   * CRITICAL: geometry.ts clamps finalFloorArea = Math.max(0, floorArea + floorAreaDelta),
   * so calling calculateRoomGeometry and checking floorArea < 0 can never trigger.
   * We must compute the UNCLAMPED value here to detect negative-intent cases.
   *
   * Replicates the relevant portion of the engine (geometry.ts lines 26, 83-116):
   *   floorArea = length * width
   *   floorAreaDelta = sum of (eW * eD) for isFullHeight niches  (positive)
   *                  - sum of (eW * eD) for isFullHeight ledges   (negative)
   * A ledge wider/deeper than the room will make unclampedFloorArea < 0.
   */
  const l = Math.max(0, parseSafeNumber(room.dims.length));
  const w = Math.max(0, parseSafeNumber(room.dims.width));
  const baseFloorArea = l * w;

  const elements = room.wallElements ?? [];
  let floorAreaDelta = 0;
  for (const el of elements) {
    if (el.includeInTotal === false) continue;
    if (!el.isFullHeight) continue;

    const eW = parseSafeNumber(el.width);
    const eD = parseSafeNumber(el.depth);

    if (el.type === 'niche') {
      floorAreaDelta += eW * eD;
    } else {
      // ledge: reduces floor area
      floorAreaDelta -= eW * eD;
    }
  }

  const unclampedFloorArea = baseFloorArea + floorAreaDelta;
  if (unclampedFloorArea < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dims', 'length'],
      message: 'computed_floor_area_negative',
    });
  }
});

export const ProjectMetaSchema = z.object({
  clientName: z.string().optional(),
  clientPhone: z.string().optional(),
  clientAddress: z.string().optional(),
  clientNip: z.string().optional(),
  contractorName: z.string().optional(),
  contractorPhone: z.string().optional(),
  contractorAddress: z.string().optional(),
  contractorNip: z.string().optional(),
  logoBase64: z.string().optional(),
  contractNumber: z.string().optional(),
  date: z.string().optional(),
  vatRate: z.number().optional().default(VAT_RATE_DEFAULT),
});

export const MaterialNormSchema = z.object({
  materialId: z.string().default(''),
  norm: z.coerce.number().default(0),
  /** unitType key for area resolution: 'm2_floor'|'m2_wall'|'m2_ceiling'|'mp'|'pcs' etc.
   *  Absent → buildBOM defaults to 'pcs' (norm applies once). Backward-compatible optional. */
  surfaceType: z.string().optional(),
});

/** Material catalog entry — a named consumable with a unit price (MAT-01). */
export const MaterialSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  nameKey: z.string().optional(),
  /** Display unit: 'kg', 'm²', 'szt', 'mb', etc. */
  unit: z.string().default(''),
  /** Price per unit (PLN or current currency). Clamped to ≥0 by UI layer, not here. */
  pricePerUnit: z.coerce.number().default(0),
  categoryId: z.string().optional(),
});

/**
 * A single BOM line produced by buildBOM.
 * normIsManual / qtyIsManual use .default(false) (not .optional()) so the field
 * is always present after parse — mirrors priceIsManual/nameIsManual on EstimateItem.
 * lineTotal is pre-rounded to 2dp by buildBOM — display with .toFixed(2), never re-round.
 */
export const BOMLineSchema = z.object({
  materialId: z.string(),
  norm: z.coerce.number().default(0),
  surfaceType: z.string().default('pcs'),
  area: z.coerce.number().default(0),
  safetyFactor: z.coerce.number().default(1),
  quantity: z.coerce.number().default(0),
  pricePerUnit: z.coerce.number().default(0),
  lineTotal: z.coerce.number().default(0),
  /** true = user overrode the norm; recompute skips this line (does not clobber). */
  normIsManual: z.boolean().default(false),
  /** true = user typed qty directly; recompute skips norm×area calculation. */
  qtyIsManual: z.boolean().default(false),
});

export const ServiceCategorySchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  nameKey: z.string().optional(),
  type: z.enum(['general', 'wall', 'floor', 'ceiling']).optional(),
});

export const ServiceSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  nameKey: z.string().optional(),
  unit: z.string().default(''),
  categoryId: z.string().default(''),
  priceLabor: z.coerce.number().default(0),
  isCustom: z.boolean().optional(),
  description: z.string().optional(),
  descriptionKey: z.string().optional(),
  unitType: z.string().optional(),
  adjustment: z.union([z.number(), z.string()]).optional(),
  materials: z.array(MaterialNormSchema).optional(),
});

export const UnitSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  nameKey: z.string().optional(),
  type: z.enum(['system', 'manual', 'custom']).default('system'),
  calcRule: z.enum(['wallArea', 'floorArea', 'perimeter', 'mp_slopes', 'manual']).default('manual'),
});

export const RecipeLayerSchema = z.object({
  serviceId: z.string().default(''),
  coefficient: z.coerce.number().default(1),
  materials: z.array(MaterialNormSchema).optional(),
});

export const RecipeSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  nameKey: z.string().optional(),
  layers: z.array(RecipeLayerSchema).default([]),
  description: z.string().optional(),
});

export const RecipeCategoryMapSchema = z.record(z.string(), z.array(RecipeSchema));

export const ProjectSnapshotStatsSchema = z.object({
  total: z.coerce.number().default(0),
  roomsCount: z.coerce.number().default(0),
  area: z.coerce.number().default(0),
});

// ---------------------------------------------------------------------------
// Document schemas — DOC-03 (contracts/acts). Additive, backward-compatible.
// All new fields on existing schemas use .optional() so old stored data parses.
// CLAUDE.md rules 11/12: never break backward-compat; no data migration needed.
// ---------------------------------------------------------------------------

/** NIP of the client/investor (Polish tax ID, e.g. "123-456-78-90") */
export const ClientDetailsSchema = z.object({
  clientName: z.string().default(''),
  clientAddress: z.string().default(''),
  clientNip: z.string().optional(),
  objectAddress: z.string().default(''),
});
export type ClientDetails = z.infer<typeof ClientDetailsSchema>;

/**
 * Payment schedule for a contract.
 * advanceDueDate/stages are freeform strings for flexibility in PL context.
 */
export const PaymentScheduleSchema = z.object({
  advancePercent: z.coerce.number().min(0).max(100).default(0),
  advanceDueDate: z.string().optional(),
  finalPaymentDays: z.coerce.number().int().min(0).default(14),
  stages: z.string().optional(),
});
export type PaymentSchedule = z.infer<typeof PaymentScheduleSchema>;

/**
 * A single closure record linking a completion act to a project line item.
 * closedQty: quantity confirmed complete in this act.
 * lineValue: net value of the closed quantity (pre-computed, not re-rounded here).
 */
export const CompletionActClosureSchema = z.object({
  itemId: z.string(),
  closedQty: z.coerce.number().min(0),
  lineValue: z.coerce.number().min(0),
});
export type CompletionActClosure = z.infer<typeof CompletionActClosureSchema>;

/**
 * A single completion act (protokół odbioru) for a contract.
 * 'partial' = mid-project act; 'final' = end-of-project act.
 */
export const CompletionActSchema = z.object({
  id: z.string(),
  actNumber: z.string().default(''),
  actDate: z.string(),
  actType: z.enum(['partial', 'final']),
  contractNumber: z.string().default(''),
  closures: z.array(CompletionActClosureSchema).default([]),
  actNetTotal: z.coerce.number().default(0),
  actVatAmount: z.coerce.number().default(0),
  actGrossTotal: z.coerce.number().default(0),
  qualityAssessment: z.string().optional(),
  defectsList: z.string().optional(),
  defectDeadline: z.string().optional(),
  remarks: z.string().optional(),
});
export type CompletionAct = z.infer<typeof CompletionActSchema>;

/**
 * Aggregate act state for a project.
 * closedByItem: itemId → total qty confirmed closed across all acts.
 * Backward-compatible: absent from stored snapshots → consumer defaults to {acts:[], closedByItem:{}}.
 */
export const ActStateSchema = z.object({
  acts: z.array(CompletionActSchema).default([]),
  closedByItem: z.record(z.string(), z.coerce.number()).default({}),
});
export type ActState = z.infer<typeof ActStateSchema>;

/**
 * SYNC-01: project-level geometry overrides used to derive an AUTO-quantity global
 * item's working quantity (calculator.ts). Absent from old stored snapshots — the
 * default {} keeps backward-compat parses identical to before this field existed.
 */
const ProjectOverridesSchema = z.object({
  floorArea: z.coerce.number().optional(),
  wallArea: z.coerce.number().optional(),
  perimeter: z.coerce.number().optional(),
});

const ProjectSnapshotCoreSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string().default(''),
  lastModified: z.string().default(''),
  rooms: z.array(RoomSchema).default([]),
  meta: ProjectMetaSchema.optional(),
  activeRoomId: z.string().nullable().default(null),
  safetyMargin: z.coerce.number().default(0),
  version: z.string().optional(),
  historyName: z.string().optional(),
  timestamp: z.string().optional(),
  stats: ProjectSnapshotStatsSchema.optional(),
  /** DOC-03: optional act state — absent in old stored snapshots (backward-compat, T-07-02) */
  actState: ActStateSchema.optional(),
  /** SYNC-01: project-level global line items — absent in old stored snapshots (backward-compat) */
  projectItems: z.array(EstimateItemSchema).default([]),
  /** SYNC-01: project-level geometry overrides — absent in old stored snapshots (backward-compat) */
  projectOverrides: ProjectOverridesSchema.default({}),
});

export const ProjectSnapshotSchema = ProjectSnapshotCoreSchema.extend({
  history: z.array(ProjectSnapshotCoreSchema).optional(),
});

// ---------------------------------------------------------------------------
// API body schemas — for @hono/zod-validator on the Worker boundary.
//
// CRITICAL: this file is imported by the Worker via @/* alias. Do NOT add
// browser-only imports (react, DOM APIs). Only zod and @/shared/lib/number
// are safe for Workers.
//
// Derived from existing schemas — NO field definitions duplicated.
// ---------------------------------------------------------------------------

/**
 * Body schema for PUT /api/projects/:id
 * The Worker stores the validated body as JSON in projects.data.
 * `history` is stripped on the write path (stored separately as project_versions).
 *
 * Adds `baseVersion` for server-authoritative optimistic concurrency (SYNC-02).
 * baseVersion = server-assigned updated_at the client last received.
 * null = new project (never synced to server); string = ISO-8601 from last successful sync.
 * Server compares against D1 updated_at: if server newer → 409 Conflict.
 */
export const ProjectUpdateBodySchema = ProjectSnapshotSchema.extend({
  /**
   * Optimistic concurrency token: the server-assigned updated_at the client last received.
   * null = new project (never synced to server); string = ISO-8601 from last successful sync.
   * Server compares against D1 updated_at: if server newer → 409 Conflict.
   * Optional for backward compatibility — absent/null triggers blind upsert (same as before).
   */
  baseVersion: z.string().datetime().nullable().optional(),
});

/**
 * Body schema for POST /api/projects/:id/versions
 * A version snapshot is the core shape without the history array
 * (history would be circular — each version IS a snapshot, not a snapshot of snapshots).
 */
export const ProjectVersionBodySchema = ProjectSnapshotSchema.omit({ history: true });

/**
 * Body schema for PUT /api/catalog/overrides
 * Represents a per-user override for a single service catalog entry.
 */
export const CatalogOverrideBodySchema = z.object({
  /** The service ID being overridden — must reference a valid catalog service_id */
  service_id: z.string().min(1),
  /** Optional override for the labor price (in PLN or EUR). Must be ≥0 — negative prices rejected at the Worker boundary (CR-01). */
  price_labor: z.coerce.number().min(0).optional(),
  /** Optional override for the display name. min(1) rejects "" at the Worker boundary (WR-01). */
  name: z.string().min(1).optional(),
  /** Whether the name override was set manually by the user (vs synced from catalog) */
  name_is_manual: z.boolean().optional(),
});

/**
 * GET response item schema for /api/catalog/overrides
 * Mirrors ParsedCatalogOverride from functions/lib/db.ts — defined here on the client
 * boundary to avoid crossing the Worker/browser import line (Pitfall 4 from research).
 *
 * Used by:
 *  - useServiceStore.applyServerOverrides (merge action, 12-01)
 *  - catalogOverridesLoader fetch validator (12-03, V5 input validation)
 */
export const CatalogOverrideSchema = z.object({
  /** UUID row id — stable across upserts */
  id: z.string(),
  /** References id field in SERVICES_DB / rawServices (e.g. 's_demo_wallpaper') */
  service_id: z.string(),
  /** Labor price override in PLN/EUR; null = no price override */
  price_labor: z.number().nullable(),
  /** Display name override; null = no name override. min(1) rejects "" — empty string would
   *  blank the service label in every UI surface (WR-01). */
  name: z.string().min(1).nullable(),
  /** true = user intentionally set the name; prevents i18n from overriding it */
  name_is_manual: z.boolean(),
  /** ISO-8601 server-assigned timestamp of last upsert */
  updated_at: z.string(),
});

export type CatalogOverride = z.infer<typeof CatalogOverrideSchema>;

/**
 * GET /api/catalog/overrides response schema.
 * Used by catalogOverridesLoader for V5 input validation at the fetch boundary.
 */
export const CatalogOverridesResponseSchema = z.object({
  overrides: z.array(CatalogOverrideSchema),
});

/**
 * Response schema for GET /api/projects (SYNC-03, Phase 36 plan 05 — hydration).
 * Mirrors listProjects()'s returned row shape (functions/lib/db.ts) exactly — `id`
 * accepts string|number matching ProjectSnapshotCoreSchema.id, composed rather than
 * redefined (file-wide "NO field definitions duplicated" rule).
 */
export const ProjectsIndexResponseSchema = z.object({
  projects: z.array(
    z.object({
      id: ProjectSnapshotCoreSchema.shape.id,
      name: z.string(),
      last_modified: z.string(),
      schema_version: z.number(),
    }),
  ),
});

/**
 * Response schema for GET /api/projects/:id (SYNC-03, Phase 36 plan 05 — hydration).
 * Mirrors ParsedProject (functions/lib/db.ts) — `data` is exactly ProjectSnapshotSchema,
 * the full snapshot hydration merges into the local library via mergeProjectIntoLibrary.
 */
export const ProjectDetailResponseSchema = z.object({
  project: z.object({
    id: ProjectSnapshotCoreSchema.shape.id,
    name: z.string(),
    last_modified: z.string(),
    updated_at: z.string(),
    schema_version: z.number(),
    data: ProjectSnapshotSchema,
  }),
});

export type ProjectUpdateBody = z.infer<typeof ProjectUpdateBodySchema>;
export type ProjectVersionBody = z.infer<typeof ProjectVersionBodySchema>;
export type CatalogOverrideBody = z.infer<typeof CatalogOverrideBodySchema>;
export type ProjectsIndexResponse = z.infer<typeof ProjectsIndexResponseSchema>;
export type ProjectDetailResponse = z.infer<typeof ProjectDetailResponseSchema>;

// ---------------------------------------------------------------------------
// User services schemas (Phase 13 CAT-03) — defined client-side to avoid
// crossing the Worker/browser import boundary (same pattern as catalog override
// schemas above). Consumed by:
//   - 13-02: Worker route zValidator imports UserServiceBodySchema
//   - 13-05: browser loader imports UserServicesResponseSchema
//   - 13-03: store maps UserService → Service
// ---------------------------------------------------------------------------

/**
 * Body schema for PUT /api/catalog/services/:id
 * Represents a per-user custom service upsert payload.
 */
export const UserServiceBodySchema = z.object({
  /** Display name — .min(1) rejects "" (WR-01: empty name would blank service label) */
  name: z.string().min(1),
  /** Display unit string: 'm2_wall', 'mp', 'pcs', 'm2_floor', etc. */
  unit: z.string().min(1),
  /** Optional category id (free text, no FK constraint) */
  category_id: z.string().nullable().optional(),
  /** Geometry calc rule ('m2_wall'|'m2_floor'|'mp'|'mp_slopes'|'pcs'|null) */
  unit_type: z.string().nullable().optional(),
  /** Labor price in PLN/EUR — .min(0) rejects negative (CR-01) */
  price_labor: z.coerce.number().min(0),
});

export type UserServiceBody = z.infer<typeof UserServiceBodySchema>;

/**
 * GET response item schema for /api/catalog/services
 * Mirrors ParsedUserService from functions/lib/db.ts — defined here on the client
 * boundary to avoid crossing the Worker/browser import line.
 *
 * Used by:
 *  - useServiceStore.applyCustomServices (13-03)
 *  - catalogOverridesLoader loadCustomServices fetch validator (13-05, V5 input validation)
 */
export const UserServiceSchema = z.object({
  id: z.string(),
  // IN-03: .min(1) defence-in-depth — mirrors UserServiceBodySchema write constraint.
  // Rejects empty-name rows that might exist from migration bugs or direct DB access
  // before they reach applyCustomServices and blank the display label.
  name: z.string().min(1),
  unit: z.string(),
  category_id: z.string().nullable(),
  unit_type: z.string().nullable(),
  // IN-03: .min(0) defence-in-depth — mirrors UserServiceBodySchema write constraint.
  // Rejects negative-price rows at the fetch boundary before they reach applyCustomServices.
  price_labor: z.number().min(0),
  /** ISO-8601 server-assigned timestamp of last upsert — required for LWW (CAT-05) */
  updated_at: z.string(),
});

export type UserService = z.infer<typeof UserServiceSchema>;

/**
 * GET /api/catalog/services response schema.
 * Used by loadCustomServices for V5 input validation at the fetch boundary.
 */
export const UserServicesResponseSchema = z.object({
  services: z.array(UserServiceSchema),
});

// ---------------------------------------------------------------------------
// Sync batch schemas — shared by browser client and Worker (Worker-safe).
//
// Used by POST /api/sync/batch (plan 04-02).
// 40-change cap encodes the D1 batch budget per phase 4 security note.
// ---------------------------------------------------------------------------

/**
 * A single change record from the client's sync queue.
 * schemaVersion must be literal 1 for the current protocol generation.
 */
export const ChangeRecordSchema = z.object({
  /** Unique ID for this change record (UUID) */
  id: z.string().min(1),
  /** The project this change belongs to */
  projectId: z.string().min(1),
  /** The room this change targets (optional for project-level changes) */
  roomId: z.string().optional(),
  /** The type of change */
  type: z.enum(['upsert_room', 'delete_room', 'upsert_project_meta', 'upsert_project_items']),
  /** The changed payload — validated at a higher level based on type */
  payload: z.unknown(),
  /** ISO-8601 timestamp when the change was enqueued (informational; NOT used for conflict winner) */
  timestamp: z.string().datetime(),
  /** Protocol schema version — must be literal 1 for the current generation */
  schemaVersion: z.literal(1),
  /**
   * Server-authoritative optimistic concurrency token (optional — absent or null for new/offline rooms).
   * The server-assigned version (ISO-8601) the client held for this room when it made the edit.
   * Server compares against the room's stored serverVersion:
   *   - null/absent → new room, always apply.
   *   - matches stored serverVersion → apply (no concurrent server change).
   *   - differs from stored serverVersion → server version wins, stale change is skipped.
   * Client wall-clock (timestamp) is NEVER used for the conflict-winner decision.
   */
  baseVersion: z.string().datetime().nullable().optional(),
});

/**
 * Payload shape for a ChangeRecord of type `upsert_project_items` (SYNC-02, Phase 36).
 * Carries the project-level GLOBAL item layer only — composed from the existing
 * sub-schemas with no field redefinition (file-wide "NO field definitions duplicated" rule).
 *
 * Whole-value-replacement semantics (locked in 36-CONTEXT.md Area 2): on apply, the
 * server overwrites projectItems/projectOverrides/safetyMargin wholesale — including
 * an EMPTY projectItems array, which is a real value (a user who deleted every global
 * item must have that deletion reach the server).
 *
 * `meta` (client/contractor/VAT) deliberately does NOT live here — it rides the
 * repaired `upsert_project_meta` payload below. One writer per field.
 */
export const ProjectItemsPayloadSchema = z.object({
  projectItems: z.array(EstimateItemSchema),
  projectOverrides: ProjectOverridesSchema.default({}),
  safetyMargin: z.coerce.number().default(0),
});

/**
 * Payload shape for a ChangeRecord of type `upsert_project_meta` (repaired, Phase 36).
 * `name` keeps the existing top-level project-name semantics the handler already
 * implements; `meta` is the previously-dropped client/contractor/VAT block.
 */
export const ProjectMetaPayloadSchema = z.object({
  name: z.string().optional(),
  meta: ProjectMetaSchema.optional(),
});

/**
 * Body schema for POST /api/sync/batch
 * Enforces 1..40 changes per batch (D1 batch budget).
 */
export const SyncBatchBodySchema = z.object({
  changes: z.array(ChangeRecordSchema).min(1).max(40),
});

/**
 * WR-04 fix (36-REVIEW.md): lenient OUTER-shape schema for POST /api/sync/batch.
 * Validates only the `changes` array's presence and count (1..40, same D1 budget
 * as SyncBatchBodySchema) — each item is deliberately `z.unknown()` here.
 *
 * Rationale: SyncBatchBodySchema validates every item against ChangeRecordSchema
 * as part of one Zod pass, so a single malformed record (e.g. a non-ISO
 * timestamp, a schemaVersion drift, or manual IDB tampering) fails validation
 * for the WHOLE request — 400ing the batch and stalling every OTHER
 * otherwise-valid record behind the poison record indefinitely (the client
 * retries the same chunk, same malformed record first, forever). This schema
 * lets the route handler validate each item individually against
 * ChangeRecordSchema and report a malformed item via the existing `rejected`
 * contract instead, so valid sibling records in the same chunk still sync.
 */
export const SyncBatchRawBodySchema = z.object({
  changes: z.array(z.unknown()).min(1).max(40),
});

/**
 * A single change record the server could not apply.
 * `reason` is built ONLY from Zod issue `message` values (never error.stack,
 * never a raw D1 driver string) — T-04-07 mitigation.
 */
export const RejectedChangeSchema = z.object({
  id: z.string().min(1),
  reason: z.string(),
});

/**
 * Response schema for POST /api/sync/batch
 * Returns the merged authoritative state for affected projects, plus any
 * change records the server rejected (CR-01 fix — never a silent drop).
 */
export const SyncBatchResponseSchema = z.object({
  merged: z.array(ProjectSnapshotSchema),
  syncedAt: z.string().datetime(),
  rejected: z.array(RejectedChangeSchema),
});

export type ChangeRecord = z.infer<typeof ChangeRecordSchema>;
export type SyncBatchBody = z.infer<typeof SyncBatchBodySchema>;
export type SyncBatchResponse = z.infer<typeof SyncBatchResponseSchema>;
export type RejectedChange = z.infer<typeof RejectedChangeSchema>;
export type ProjectItemsPayload = z.infer<typeof ProjectItemsPayloadSchema>;
export type ProjectMetaPayload = z.infer<typeof ProjectMetaPayloadSchema>;

// ---------------------------------------------------------------------------
// Billing schemas — Phase 8 SaaS monetization.
// Worker-safe: no browser/React imports below.
// ---------------------------------------------------------------------------

/**
 * Body schema for POST /api/billing/checkout
 * Validates the billing interval before any Stripe API call.
 * Rejects any interval outside the literal union (T-08-08 — tampering mitigation).
 */
export const BillingCheckoutBodySchema = z.object({
  /** The billing interval for the Pro subscription */
  interval: z.enum(['monthly', 'annual']),
});

export type BillingCheckoutBody = z.infer<typeof BillingCheckoutBodySchema>;

// ---------------------------------------------------------------------------
// Analytics schemas (Phase 18 FUNNEL-01) — Worker-safe: no browser/React imports.
//
// CLIENT_EVENT_ALLOWLIST: only events that originate from the browser client.
// Server-side events (signup, checkout_started, paid, churn) are written
// internally via insertAnalyticsEvent and are NOT in the client allowlist —
// accepting them from the client would enable double-counting (Correction 1).
// ---------------------------------------------------------------------------

/** Events the authenticated browser client is permitted to POST to /api/events */
export const CLIENT_EVENT_ALLOWLIST = ['app_open', 'activation', 'export'] as const;
export type ClientEvent = (typeof CLIENT_EVENT_ALLOWLIST)[number];

/**
 * PII keys that are forbidden in the `props` field of POST /api/events.
 *
 * Rationale: analytics events must never carry personally-identifiable information.
 * The server-side SAFE_PROPS_KEYS whitelist in functions/api/routes/events.ts strips unknown
 * keys as a defence-in-depth measure, but the ROADMAP Phase 23 hard constraint
 * (SC-4) requires that PII keys are hard-rejected at the schema boundary with HTTP 400
 * so that misbehaving clients receive an explicit error rather than a silent strip.
 *
 * Keys are checked case-insensitively (lowercased before includes()) so that
 * 'Email', 'EMAIL', 'email' are all blocked.
 */
export const PII_BLOCKLIST_KEYS = [
  'email',
  'name',
  'project_name',
  'phone',
  'address',
  'full_name',
  'first_name',
  'last_name',
] as const;

/**
 * Body schema for POST /api/events
 * - event: must be one of the client-permitted event names (z.enum enforced → 400 on unknown)
 * - props: optional Record; defaults to {}; PII keys in PII_BLOCKLIST_KEYS are rejected
 *   at schema boundary with a 400 error (SC-4). Server additionally strips unknown keys
 *   that are not in SAFE_PROPS_KEYS (defence-in-depth).
 *
 * Security: user_id is NOT in this schema — it is sourced server-side from c.get('userId')
 * (set by requireAuth from the httpOnly session cookie). The outer object is `.strict()`, so
 * any userId (or other unknown top-level) field in the body is REJECTED with 400 by zValidator
 * before the handler runs — not silently stripped.
 */
export const EventsBodySchema = z
  .object({
    event: z.enum(CLIENT_EVENT_ALLOWLIST),
    props: z
      .record(z.string(), z.unknown())
      .optional()
      .default({})
      .superRefine((props, ctx) => {
        // Reject any props key that matches a PII blocklist entry (case-insensitive).
        // This enforces the ROADMAP SC-4 hard constraint: PII in props → 400, not 202.
        for (const key of Object.keys(props)) {
          if ((PII_BLOCKLIST_KEYS as readonly string[]).includes(key.toLowerCase())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [key],
              message: `PII key '${key}' is not permitted in analytics props`,
            });
          }
        }
      }),
  })
  .strict();
// T-23-01 mitigation: closed outer object — unknown top-level body keys (e.g. userId, event_type)
// are REJECTED with 400 by zValidator, not silently stripped. Prevents analytics event injection /
// mass-assignment. The inner props record remains open (z.record) by design; SAFE_PROPS_KEYS
// strips unknown non-PII props server-side as defence-in-depth.

export type EventsBody = z.infer<typeof EventsBodySchema>;

export const storageSchemaMap: Record<string, z.ZodType> = {
  bg2_rooms: z.array(RoomSchema),
  bg2_project_meta: ProjectMetaSchema,
  bg2_active_project_id: z.union([z.string(), z.number(), z.null()]),
  bg2_safetyMargin: z.coerce.number(),
  bg2_currency: z.string(),
  bg2_activeRoomId: z.string().nullable(),
  bg2_services: z.array(ServiceSchema),
  bg2_categories: z.array(ServiceCategorySchema),
  bg2_units: z.array(UnitSchema),
  bg2_recipes: RecipeCategoryMapSchema,
  bg2_library: z.array(ProjectSnapshotSchema),
  /** Material catalog store (MAT-01). Array capped at 500 entries to mitigate T-06-DOS. */
  'estimatix-materials': z.object({ state: z.object({ materials: z.array(MaterialSchema).max(500) }) }),
};